import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthStore } from '@vantage-web/core-data';
import { SKIP_INTERCEPTOR } from './skip-interceptor.token';

/**
 * Attaches the territory-switcher scope as the `X-Territory-Id` header.
 *
 * When a cross-territory user (corporate / SUPER_ADMIN / NAM) scopes into a single territory via
 * the top-bar switcher, `AuthStore.activeTerritoryId` holds the chosen id. Sending it lets the
 * backend (`get_auth` → the `app.scoped_territory_id` RLS GUC, read by `app_scoped_territory_id()`)
 * narrow visibility to that one territory — without it, the selection is frontend-only and the API
 * still returns every territory's rows. When unscoped (`null` — corporate "All Territories", or a
 * single-territory user) no header is sent and behavior is unchanged.
 *
 * Scope can only ever NARROW: the backend validates the id (corporate, or a territory the caller
 * belongs to) and RLS clamps it, so a forged/foreign id can never widen access.
 */
export function territoryContextInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (req.context.get(SKIP_INTERCEPTOR)) {
    return next(req);
  }

  const territoryId = inject(AuthStore).activeTerritoryId();

  return next(
    territoryId
      ? req.clone({ setHeaders: { 'X-Territory-Id': territoryId } })
      : req,
  );
}
